import { A, useLocation, useNavigate } from "@solidjs/router";
import { createSignal } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { type LoginLocationState, performLogin, sanitizeTotp } from "./loginFlow.ts";

export function Login() {
  const { client, setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [totpCode, setTotpCode] = createSignal("");
  const [error, setError] = createSignal<unknown | null>(null);
  const [busy, setBusy] = createSignal(false);

  async function onSubmit(e: Event) {
    e.preventDefault();
    // Re-entrancy guard: a disabled button is not enough (Enter-key repeats,
    // programmatic submits), and a duplicate login would mint a second session.
    if (busy()) return;
    setError(null);
    setBusy(true);
    try {
      await performLogin(
        client,
        { email: email(), password: password(), totpCode: totpCode() },
        location.state as LoginLocationState | null,
        { setSession, navigate: (to) => navigate(to, { replace: true }) },
      );
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="auth-shell">
      <form onSubmit={onSubmit} class="card auth-card">
        <h1>Developer portal</h1>
        <p class="muted">
          Sign in to issue API keys and build on the agent rail — email, password, and your
          authenticator code.
        </p>
        <ErrorBanner error={error()} />
        <label for="email">Email</label>
        <input
          id="email"
          type="email"
          autocomplete="email"
          value={email()}
          onInput={(e) => setEmail(e.currentTarget.value)}
          required
        />
        <label for="password">Password</label>
        <input
          id="password"
          type="password"
          autocomplete="current-password"
          value={password()}
          onInput={(e) => setPassword(e.currentTarget.value)}
          required
        />
        <label for="totp">6-digit code</label>
        <input
          id="totp"
          inputmode="numeric"
          pattern="[0-9]{6}"
          autocomplete="one-time-code"
          value={totpCode()}
          onInput={(e) => setTotpCode(sanitizeTotp(e.currentTarget.value))}
          required
        />
        <button type="submit" class="primary auth-submit" disabled={busy()}>
          {busy() ? "Signing in…" : "Sign in"}
        </button>
        <p class="muted auth-alt">
          New here? <A href="/register">Create an account</A>
        </p>
      </form>
    </div>
  );
}
